/**
 * usePlatformMessageBridge.ts
 *
 * React hook for the Platform — sends commands to sub-project iframes
 * via postMessage and awaits their responses via a pending-request map.
 *
 * Identical pattern to VinGroupHub's useMessageBridge, extracted here
 * so the Platform can operate independently.
 */

import { useCallback, useEffect, useRef } from 'react';

/* ─── Types ─── */
export interface AgentCommand {
    type: 'agent_command';
    requestId: string;
    action: string;
    params: Record<string, any>;
    timestamp: number;
}

export interface CommandResult {
    type: 'command_result';
    requestId: string;
    success: boolean;
    data?: any;
    error?: string;
    timestamp: number;
}

interface PendingRequest {
    resolve: (result: CommandResult) => void;
    reject: (err: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
}

const DEFAULT_TIMEOUT_MS = 8000;

export function usePlatformMessageBridge() {
    const pendingRequests = useRef(new Map<string, PendingRequest>());

    /* listen for command_result messages from iframes */
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            const data = event.data;
            if (!data || data.type !== 'command_result') return;

            const pending = pendingRequests.current.get(data.requestId);
            if (!pending) return;

            clearTimeout(pending.timeout);
            pending.resolve(data as CommandResult);
            pendingRequests.current.delete(data.requestId);
        };

        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
            // clean up timeouts on unmount
            pendingRequests.current.forEach((p) => clearTimeout(p.timeout));
            pendingRequests.current.clear();
        };
    }, []);

    /**
     * Send a command to the given iframe and return a promise that resolves
     * when the iframe posts back a command_result with the matching requestId.
     */
    const sendCommand = useCallback(
        (
            iframe: HTMLIFrameElement | null,
            action: string,
            params: Record<string, any> = {},
            timeoutMs = DEFAULT_TIMEOUT_MS,
        ): Promise<CommandResult> => {
            return new Promise((resolve, reject) => {
                if (!iframe?.contentWindow) {
                    reject(new Error('Iframe not available'));
                    return;
                }

                const requestId = crypto.randomUUID();
                const timeout = setTimeout(() => {
                    pendingRequests.current.delete(requestId);
                    reject(new Error(`Timeout: no response from iframe for action "${action}" after ${timeoutMs}ms`));
                }, timeoutMs);

                pendingRequests.current.set(requestId, { resolve, reject, timeout });

                const command: AgentCommand = {
                    type: 'agent_command',
                    requestId,
                    action,
                    params,
                    timestamp: Date.now(),
                };

                iframe.contentWindow.postMessage(command, '*');
            });
        },
        [],
    );

    return { sendCommand };
}
